import { PickCircle } from "@/components/PickCircle";
// Outlook-style hourly time-grid used for the Week and Day calendar views.
// Places each rodeo performance at its start time. Untimed/TBD perfs go in a top row.
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const dot = (r) => (r.associations || []).includes("CPRA") ? "#C8000F" : (r.associations || []).includes("PRCA") ? "#1D4ED8" : "#9333EA";
const tint = (r) => (r.associations || []).includes("CPRA") ? "#FDECEE" : (r.associations || []).includes("PRCA") ? "#EAF0FE" : "#F3EAFB";

function parseTime(t) {
  if (!t) return null;
  const m = String(t).match(/(\d{1,2})(?::(\d{2}))?\s*([AaPp][Mm])?/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const ap = m[3] ? m[3].toLowerCase() : null;
  if (ap === "pm" && h !== 12) h += 12;
  if (ap === "am" && h === 12) h = 0;
  if (h < 0 || h > 23) return null;
  return { h, min };
}
const hourLabel = (h) => { const ap = h < 12 ? "AM" : "PM"; const hh = h % 12 === 0 ? 12 : h % 12; return `${hh} ${ap}`; };
const todayISO = new Date().toISOString().slice(0, 10);

function Chip({ item, onPick, onEntry, onEntered, choices, highlightId, wide }) {
  const { rodeo: r, perf: p } = item;
  const ch = choices[r.id] || {};
  const num = ch.pref1 === p.id ? 1 : ch.pref2 === p.id ? 2 : null;
  const ent = !!(ch.entered || {})[p.id];
  const hot = highlightId === r.id;
  return (
    <div data-entry={r.id}
      className={`relative rounded-md mb-1 border-l-[3px] ${hot ? "ring-2 ring-[#C8000F]" : ""}`}
      style={{ background: ent ? "#DCF5D6" : num === 1 ? "#EAF3EC" : tint(r), borderColor: ent || num === 1 ? "#166534" : dot(r) }}>
      <button onClick={() => onEntry(r)} data-testid={`tg-chip-${p.date}-${r.id}-${p.id}`}
        title="Click to highlight & move to top of the list"
        className="w-full text-left px-1.5 py-1 pr-12 hover:brightness-95 transition">
        <div className="flex items-center gap-1">
          {p.time && <span className="text-[9px] font-bold text-slate-500 shrink-0">{p.time}</span>}
          <span className={`text-[9px] font-bold px-1 rounded ${p.type === "slack" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>{p.type === "slack" ? "SLACK" : "PERF"}</span>
        </div>
        <div className={`font-semibold text-slate-800 leading-tight ${wide ? "text-[13px]" : "text-[11px] truncate"}`}>{r.name}</div>
        {wide && <div className="text-[11px] text-slate-500">{r.city}, {r.state} · {p.label}</div>}
      </button>
      <div className="absolute top-1 right-1">
        <PickCircle num={num} entered={ent} onClick={() => onPick(r, p)} onToggleEntered={onEntered ? () => onEntered(r, p) : undefined} testid={`tg-pick-${p.date}-${r.id}-${p.id}`} size={16} />
      </div>
    </div>
  );
}

export function TimeGrid({ days, perfsByDay, choices, onPick, onEntry, onEntered, highlightId }) {
  // Gather timed / untimed items and hour bounds across the visible days.
  const timed = {}; const untimed = {};
  let minH = 23, maxH = 0, hasTimed = false;
  for (const day of days) {
    timed[day] = {}; untimed[day] = [];
    for (const item of (perfsByDay[day] || [])) {
      const pt = parseTime(item.perf.time);
      if (pt) { hasTimed = true; minH = Math.min(minH, pt.h); maxH = Math.max(maxH, pt.h); (timed[day][pt.h] = timed[day][pt.h] || []).push({ ...item, _min: pt.min }); }
      else untimed[day].push(item);
    }
  }
  if (!hasTimed) { minH = 8; maxH = 18; }
  minH = Math.max(0, minH - 0); maxH = Math.min(23, maxH);
  const hours = []; for (let h = minH; h <= maxH; h++) hours.push(h);
  const anyUntimed = days.some((d) => untimed[d].length);
  const wide = days.length === 1;
  const cols = `64px repeat(${days.length}, minmax(0, 1fr))`;

  return (
    <div className="border border-[#E4E7EB] rounded-lg overflow-hidden" data-testid="time-grid">
      {/* Header: day columns */}
      <div className="grid sticky top-0 z-10 bg-[#F7F8FA]" style={{ gridTemplateColumns: cols }}>
        <div className="border-b border-r border-[#E4E7EB]" />
        {days.map((d) => {
          const dt = new Date(d + "T00:00:00");
          const isToday = d === todayISO;
          return (
            <div key={d} className="border-b border-r border-[#E4E7EB] text-center py-1.5" data-testid={`tg-head-${d}`}>
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{DOW[dt.getDay()]}</div>
              <div className={`text-sm font-bold ${isToday ? "text-white bg-[#C8000F] rounded-full w-6 h-6 flex items-center justify-center mx-auto" : "text-slate-700"}`}>{dt.getDate()}</div>
              {wide && <div className="text-[11px] text-slate-400">{dt.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</div>}
            </div>
          );
        })}
      </div>

      {/* Untimed / TBD row */}
      {anyUntimed && (
        <div className="grid" style={{ gridTemplateColumns: cols }}>
          <div className="border-b border-r border-[#E4E7EB] bg-[#FBFCFD] text-[9px] font-bold uppercase tracking-wide text-slate-400 text-right pr-2 py-1.5">Time TBD</div>
          {days.map((d) => (
            <div key={d} className="border-b border-r border-[#E4E7EB] p-1 min-h-[40px]">
              {untimed[d].map((it, i) => <Chip key={i} item={it} onPick={onPick} onEntry={onEntry} onEntered={onEntered} choices={choices} highlightId={highlightId} wide={wide} />)}
            </div>
          ))}
        </div>
      )}

      {/* Hour rows */}
      {hours.map((h) => (
        <div key={h} className="grid" style={{ gridTemplateColumns: cols }}>
          <div className="border-b border-r border-[#E4E7EB] bg-[#FBFCFD] text-[10px] font-semibold text-slate-400 text-right pr-2 pt-1">{hourLabel(h)}</div>
          {days.map((d) => {
            const items = (timed[d][h] || []).sort((a, b) => a._min - b._min);
            return (
              <div key={d} className="border-b border-r border-[#E4E7EB] p-1 min-h-[56px]" data-testid={`tg-cell-${d}-${h}`}>
                {items.map((it, i) => <Chip key={i} item={it} onPick={onPick} onEntry={onEntry} onEntered={onEntered} choices={choices} highlightId={highlightId} wide={wide} />)}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
